import { Button, Col, Image, Layout, Row, Spin, Typography, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import VideoIcon from "../../../assets/icons/jouer.svg";
import SelectedIcon from "../../../assets/icons/selected.svg";
import UnselectIcon from "../../../assets/icons/unselect.svg";
import RecordIcon from "../../../assets/icons/audio-record-light-bg.svg";
import ArrowLeftIconComponent from "../../../components/ArrowLeftIconComponent";
import DeleteMediasAlbumModalComponent from "../../../components/DeleteMediasAlbumModalComponent";
import SpinnerComponent from "../../../components/SpinnerComponent/SpinnerComponent";
import VideoPlayer from "../../../components/VideoPlayerComponent";
import VoiceChatListRow from "../../../components/VoiceChatListRow/VoiceChatListRow";
import RecordAudioModal from "../../../components/RecordAudioModal/RecordAudioModal";
import { ACCEPTED, FALLBACK_IMAGE, FILE, FILE_STATUS, THUMBNAIL_TYPES } from "../../../helpers/constant";
import { IThumbnail, IMessage } from "../../../helpers/types";
import { getQuickFileAlbumDetails, updateQuickFile } from "../../../redux/quickFile/quickFileSlice";
import { AppDispatch, StoreState } from "../../../redux/store";
import { quickFileService } from "../../../services/quickFile.service";
import { addMessageInstant, sendMessage } from "../../../redux/messages/messagesSlice";
import { authService, fileService } from "../../../services";
import { socket } from "../../../socket";
import { getVideoDurationFromUrl } from "../../../utls/FunctionsUtil";
import "./index.less";

const CreatorAlbumFeedsComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { albumId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const AlbumFeeds = useSelector((state: any) => state.quickFile.item);
  const shouldFetch = useRef(true);
  const [select, setSelect] = useState<any>([]);
  const [edit, setEdit] = useState(false);
  const [modelIsOpen, setModalIsOpen] = useState(false);
  const [queryParams, setQueryParams] = useState({
    id: "",
    conversationId: "",
    messageId: "",
    receiverId: "",
    collectionTitle: "",
    block: "",
    scriptId: "",
  });
  const { selectedConversation } = useSelector((state: StoreState) => state.messages);
  const [selectSendImages, setSelectSendImages] = useState<any>([]);
  const [visible, setVisible] = useState<boolean>(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string>("");
  const [videoDurations, setVideoDurations] = useState<{ [fileId: string]: string }>({});
  const [isRecordOpen, setIsRecordOpen] = useState(false);

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString();
  const isAudioAlbum = AlbumFeeds?.type === "audio";

  useEffect(() => {
    const fetchDurations = async () => {
      if (!AlbumFeeds?.files) return;
      const durations: { [fileId: string]: string } = {};
      await Promise.all(
        AlbumFeeds.files.map(async (item: any) => {
          if (item.url?.includes(".mp4")) {
            const duration = await getVideoDurationFromUrl(item.url);
            durations[item.fileId] = duration;
          }
        })
      );
      setVideoDurations(durations);
    };
    fetchDurations();
  }, [AlbumFeeds?.files]);

  useEffect(() => {
    if (shouldFetch.current) {
      shouldFetch.current = false;
      dispatch(getQuickFileAlbumDetails(albumId)).then(() => {
        if (AlbumFeeds?.files?.length > 0) {
          const data = getMatchingFileIds();
          setSelectSendImages(data?.array);
          setSelect(data?.objects);
        }
      });
    }

    function handleFileUpdated(data: { fileId: string; absolutePath: string; thumbnails: any; status: string }) {
      dispatch(updateQuickFile({ albumId, data }));
    }

    socket.on("file-updated", handleFileUpdated);

    return () => {
      socket.off("file-updated", handleFileUpdated);
    };
  }, []);

  const sendAudioAsMessage = async (file: any) => {
    if (!queryParams.conversationId) {
      message.error("No conversation selected");
      return;
    }

    try {
      // Create message with already uploaded file
      const newMessage: IMessage = {
        temp_id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        conversationId: queryParams.conversationId,
        medias: [
          {
            fileId: file.fileId || file._id || "",
            isPrivate: false,
          },
        ],
        type: FILE,
        uploading: false,
      };

      (newMessage as any).isAudioFile = true;
      dispatch(addMessageInstant(newMessage));
      // Send message directly without re-uploading
      dispatch(sendMessage(newMessage));

      // message.success(t("audio-sent-successfully"));

      navigate(`/creator/conversation/${queryParams.conversationId}`);
    } catch (error) {
      console.error("Error sending audio:", error);
      message.error(t("back-error-message"));
    }
  };

  const selectFeed = (id: any) => {
    if (edit) {
      const isSelected = select.some((item: any) => item.id === id);
      if (queryParams.id) {
        if (queryParams.id !== "unlock-link" && queryParams.id !== "post") {
          if (isSelected) {
            setSelectSendImages(selectSendImages.filter((item: any) => item !== id));
          } else {
            let items = selectSendImages;
            items.push(id);
            setSelectSendImages(items);
          }
        } else {
          setSelectSendImages([id]);
        }
      }
      if (isSelected) {
        const newSelect = select.filter((item: any) => item.id !== id);
        setSelect(newSelect);
      } else {
        if (queryParams.id !== "unlock-link" && queryParams.id !== "post") {
          setSelect([...select, { id }]);
        } else {
          setSelect([{ id }]);
        }
      }
    }
  };

  const handleVideoIconClick = (url: string, isVisible: boolean) => {
    setCurrentVideoUrl(isVisible ? url : "");
    setVisible(isVisible);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id") || "";
    const conversationId = params.get("conversationId") || "";
    const receiverId = params.get("receiverId") || "";
    const messageId = params.get("messageId") || "";
    const collectionTitle = params.get("collectionTitle") || "";
    const block = params.get("block") || "";
    const scriptId = params.get("scriptId") || "";
    if (id) {
      setEdit(true);
    }
    setQueryParams({ id, conversationId, receiverId, messageId, collectionTitle, block, scriptId });
  }, []);

  async function deleteFiles() {
    let files: any = [];
    select.forEach((item: any) => {
      files.push(item.id);
    });
    const payload = {
      albumId: albumId,
      filesId: files,
    };
    try {
      quickFileService
        .deleteAlbumFiles(payload)
        .then((res) => {
          message.success(t("deleted-files-success-message"));
          setModalIsOpen(false);
          setEdit(false);
          setSelect([]);
          dispatch(getQuickFileAlbumDetails(albumId));
        })
        .catch((err) => {
          message.error("Something went wrong");
          console.log("err", err);
        });
    } catch (err) {
      message.error("Something went wrong");
      console.error("err", err);
    }
  }

  const handleSendClick = async () => {
    if (queryParams.id === "conversation" && isAudioAlbum) {
      // For audio albums, this is handled by onSelectAndSend in VoiceChatListRow
      return;
    } else if (queryParams.id === "conversation" && !isAudioAlbum) {
      // Send selected photos/videos directly without re-uploading
      try {
        for (const fileId of selectSendImages) {
          const file = AlbumFeeds?.files?.find((f: any) => f.fileId === fileId);
          if (file) {
            const newMessage: IMessage = {
              temp_id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              conversationId: queryParams.conversationId,
              medias: [
                {
                  fileId: file.fileId || file._id || "",
                  isPrivate: false,
                },
              ],
              type: FILE,
              uploading: false,
            };

            dispatch(addMessageInstant(newMessage));
            dispatch(sendMessage(newMessage));
          }
        }
        // message.success(t("audio-sent-successfully"));
        navigate(`/creator/conversation/${queryParams.conversationId}`);
      } catch (error) {
        console.error("Error sending media:", error);
        message.error(t("back-error-message"));
      }
      return;
    }
    
    // For all other cases, use the original navigation logic
    if (queryParams.id === "conversation") {
      navigate(`/creator/conversation/${queryParams.conversationId}?selected=${JSON.stringify(selectSendImages)}`);
    } else if (queryParams.id === "mediaPush") {
      navigate(
        `/creator/mediaPush?conversation=${queryParams?.conversationId !== ""}&conversationId=${queryParams?.conversationId}&receiverId=${queryParams?.receiverId}&messageId=${queryParams?.messageId}&selected=${JSON.stringify(selectSendImages)}`
      );
    } else if (queryParams.id === "chatScripts") {
      if (queryParams.scriptId) {
        navigate(`/creator/chat-scripts/update?id=${queryParams.scriptId}&selected=${JSON.stringify(selectSendImages)}`);
      } else {
        navigate(`/creator/chat-scripts/create?selected=${JSON.stringify(selectSendImages)}`);
      }
    } else if (queryParams.id === "unlock-link") {
      navigate(`/creator/createDecrypt?selected=${JSON.stringify(selectSendImages)}`);
    } else if (queryParams.id === "post") {
      navigate(`/creator/publication?selected=${JSON.stringify(selectSendImages)}`);
    } else if (queryParams.id === "collection") {
      const NewSelected = normalizeData(select);
      navigate(`/creator/create/collection?collectionTitle=${queryParams.collectionTitle}&selected=${JSON.stringify(NewSelected)}`);
      sessionStorage.removeItem("selected-quick-files");
    } else if (queryParams.id === "automation") {
      navigate(`/creator/message-automatic/${queryParams.block}?selected=${JSON.stringify(selectSendImages)}`);
    } else if (queryParams.id === "pendingMedia") {
      navigate(
        `/creator/conversation/${queryParams?.conversationId}?selected=${JSON.stringify(selectSendImages)}&isMedia=true&messageId=${queryParams?.messageId}`
      );
    } else if (queryParams.id === "chatScripts") {
      navigate(`/creator/chat-scripts/create?conversationId=${queryParams.conversationId}&selected=${JSON.stringify(selectSendImages)}`);
    }
  };

  const storeSelectedQuickFiles = (newFiles: string[]) => {
    const stringData = normalizeData(newFiles);
    const key = "selected-quick-files";
    sessionStorage.setItem(key, JSON.stringify(stringData));
  };

  // Function to retrieve matching file IDs
  const getMatchingFileIds = () => {
    const key = "selected-quick-files";

    // Retrieve stored files from sessionStorage
    const storedFiles = sessionStorage.getItem(key);
    if (!storedFiles) return { array: [], objects: [] };
    const parsedFiles = JSON.parse(storedFiles);

    return {
      array: parsedFiles,
      objects: parsedFiles.map((id: any) => ({ id })),
    };
  };

  const normalizeData = (data: any) => {
    if (Array.isArray(data) && data.length > 0 && data[0]?.id) {
      return data.map((item) => item.id);
    } else if (Array.isArray(data)) {
      return data;
    } else {
      console.error("Invalid data format");
      return [];
    }
  };

  return (
    <Layout className="gradient-background-blue-settings">
      <Layout.Content className="content CreatorAlbumContentWrapper background-transparent">
        <Row className="relative justify-content-center header-container-title-quick-file">
          {queryParams?.conversationId ? (
            <ArrowLeftIconComponent
              left={16}
              top={10}
              navigateTo={
                queryParams.id === "chatScripts"
                  ? `/common/quick-file?id=${queryParams?.id}&conversationId=${queryParams?.conversationId}`
                  : `/creator/quickLink?id=${queryParams?.id}&conversationId=${queryParams?.conversationId}&receiverId=${queryParams?.receiverId}&messageId=${queryParams?.messageId}`
              }
              replace={true}
            />
          ) : (
            <ArrowLeftIconComponent
              top={10}
              left={16}
              callback={() => {
                if (queryParams.id === "collection") {
                  storeSelectedQuickFiles(select);
                }
              }}
            />
          )}
          <div className="header-album-container">
            <Typography.Text className="header-album-title">{AlbumFeeds?.name}</Typography.Text>
          </div>
        </Row>
        <Row>
          <Typography.Text className="ml-20 mt-30 font-18-bold text-white-color">
            {queryParams?.id ? t("select-the-media-you-want-to-send") : !edit ? t("what-do-you-want-to-do") : t("select-media")}
          </Typography.Text>
        </Row>
        {!queryParams?.id && (
          <Row className="creatorAlbumButton w-full">
            <div className="w-full justify-center">
              {edit && select.length > 0 ? (
                <div className="button-container">
                  <Button onClick={() => setModalIsOpen(true)} className="CreatorAlbumActionButton half-width-button">
                    {t("Delete")}
                  </Button>
                  <Button
                    onClick={() => {
                      setEdit(false);
                      setSelect([]);
                    }}
                    className="CreatorAlbumActionButton half-width-button"
                  >
                    {t("cancel")}
                  </Button>
                </div>
              ) : edit ? (
                <div className="button-container">
                  <Button className="CreatorAlbumActionButton disable half-width-button" disabled>
                    {t("Delete")}
                  </Button>
                  <Button
                    onClick={() => {
                      setEdit(false);
                      setSelect([]);
                    }}
                    className="CreatorAlbumActionButton half-width-button"
                  >
                    {t("cancel")}
                  </Button>
                </div>
              ) : (
                <div className="button-container">
                  {AlbumFeeds?.files.length > 0 && (
                    <Button onClick={() => setEdit(true)} className="CreatorAlbumActionButton half-width-button">
                      {t("modify-album")}
                    </Button>
                  )}
                  <Button
                    onClick={() => navigate("/creator/AddMedia/" + albumId, { replace: true })}
                    className="CreatorAlbumActionButton half-width-button"
                  >
                    {t("add-media")}
                  </Button>
                </div>
              )}
            </div>
          </Row>
        )}
        {!queryParams?.id && (
          <Row>
            <Typography.Text className="ml-20 mb-10 mt-10 font-24-bold text-white-color">{t("media-for-your-album")}</Typography.Text>
          </Row>
        )}
        {isAudioAlbum ? (
          <div className="vc-content">
            <div className="vc-list">
              {AlbumFeeds?.files?.length > 0 &&
                AlbumFeeds.files.map((item: any) => (
                  <div className="vc-list-item" key={item.fileId || item.id}>
                    <VoiceChatListRow
                      file={item}
                      title={item.title || item.name || "Untitled Audio"}
                      date={formatDate(item.createdAt)}
                      duration={item.time || item.duration || 0}
                      albumId={albumId}
                      onClick={() => selectFeed(item.fileId)}
                      select={edit}
                      onDelete={() => {
                        dispatch(getQuickFileAlbumDetails(albumId));
                      }}
                      onSelectAndSend={(file) => {
                        // Send audio directly as message to conversation
                        if (queryParams.id === "conversation") {
                          sendAudioAsMessage(file);
                        } else {
                          // For other contexts, use the original selection logic
                          selectFeed(file.fileId);
                          requestAnimationFrame(() => {
                            handleSendClick();
                          });
                        }
                      }}
                    />
                  </div>
                ))}
              {(!AlbumFeeds?.files || AlbumFeeds.files.length === 0) && (
                <div className="empty-state">
                  <p>{t("no-files-audio")}</p>
                </div>
              )}
            </div>
            {!queryParams?.id && (
              <button className="vc-record-fab" onClick={() => setIsRecordOpen(true)}>
                <img src={RecordIcon} alt="record" />
              </button>
            )}
          </div>
        ) : (
          <Row className="fanBookmarksContent mt-5">
            {AlbumFeeds?.files?.length > 0 &&
              AlbumFeeds?.files?.map((item: any, index: any) => {
                const isSelected = [...select].some((selectedItem: any) => selectedItem.id === item.fileId);
                const isUploading: boolean = item?.status === FILE_STATUS.UPLOADING;
                const isVideo = item.url?.includes(".mp4");
                const duration = isVideo ? videoDurations[item.fileId] : null;
                return (
                  <Col onClick={() => selectFeed(item.fileId)} key={index} span={8} className="p-1 relative">
                    {edit ? (
                      <>
                        <div className="cover"></div>
                        <img
                          className="absolute selectIcon"
                          src={isSelected ? SelectedIcon : UnselectIcon}
                          alt={isSelected ? "selected" : "unselected"}
                        />
                      </>
                    ) : null}
                    <Spin spinning={isUploading} className="feedSpinner" indicator={<SpinnerComponent />} />
                    {isUploading ? (
                      <div className="imagePlaceHolder" />
                    ) : (
                      <Image
                        src={item.thumbnails?.find((thumb: IThumbnail) => thumb.type === THUMBNAIL_TYPES.CLEAR)?.absolutePath || item?.url}
                        fallback={FALLBACK_IMAGE}
                        preview={
                          !item.url?.includes(".mp4")
                            ? {
                                src: item.url,
                              }
                            : {
                                destroyOnClose: true,
                                visible: visible && currentVideoUrl === item.url,
                                onVisibleChange: (value) => handleVideoIconClick(item.url, value),
                                modalRender: () => {
                                  if (currentVideoUrl === item.url) {
                                    return (
                                      <div className="video-preview-wrapper">
                                        <VideoPlayer videoUrl={currentVideoUrl} thumbnailUrl={""} autoPlay={true} />
                                      </div>
                                    );
                                  }
                                  return undefined;
                                },
                              }
                        }
                      />
                    )}
                    {item.url?.includes(".mp4") && !isUploading && (
                      <>
                        <img
                          src={VideoIcon}
                          alt="Twizz - video icon"
                          className="albumVideoIcon"
                          onClick={() => handleVideoIconClick(item.url, true)}
                        />
                        {duration && <Typography.Text className="albumVideoDuration">{duration}</Typography.Text>}
                      </>
                    )}
                  </Col>
                );
              })}
          </Row>
        )}
        <DeleteMediasAlbumModalComponent visible={modelIsOpen} onClose={() => setModalIsOpen(false)} onDelete={deleteFiles} />
        <RecordAudioModal isOpen={isRecordOpen} onClose={() => setIsRecordOpen(false)} />
      </Layout.Content>
      {queryParams?.id && !isAudioAlbum && (
        <Layout.Footer className="CreatorAlbumFooter">
          <Row className="w-full justify-content-center">
            {select.length === 0 ? (
              <Button className={`CreatorAlbumSelectButton disable`}>{t(`Select`)}</Button>
            ) : (
              <Button className={`CreatorAlbumSelectButton `} onClick={handleSendClick}>
                {t(`Select`)}
              </Button>
            )}
          </Row>
        </Layout.Footer>
      )}
    </Layout>
  );
};

export default CreatorAlbumFeedsComponent;
